import {createTheme} from "@mui/material";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import {ReactNode, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {getBranding} from "~/store/selectors/whitelabel";
import {appTheme} from "@pinch-financial/pinch-ui-components";

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    mobile: true;
    laptop: true;
    desktop: true;
  }

  interface TypographyVariants {
    titleBarHeader: React.CSSProperties;
    copyHeader: React.CSSProperties;
    copyBody: React.CSSProperties;
    copyLegalSmall: React.CSSProperties;
    copyTextLink: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    titleBarHeader?: React.CSSProperties;
    copyHeader?: React.CSSProperties;
    copyBody?: React.CSSProperties;
    copyLegalSmall?: React.CSSProperties;
    copyTextLink?: React.CSSProperties;
  }

  interface Palette {
    titleBar?: Palette['primary'];
    titleBarGradient1?: Palette['primary'];
    titleBarGradient2?: Palette['primary'];

    tipBox?: Palette['primary'];

    copyHeader?: Palette['primary'];
    copyBody?: Palette['primary'];
    copyLegalSmall?: Palette['primary'];
    copyTextLink?: Palette['primary'];

    button?: Palette['primary'];
    label?: Palette['primary'];
  }

  interface PaletteOptions {
    titleBar?: PaletteOptions['primary'];
    titleBarGradient1?: PaletteOptions['primary'];
    titleBarGradient2?: PaletteOptions['primary'];

    tipBox?: PaletteOptions['primary'];

    copyHeader?: PaletteOptions['primary'];
    copyBody?: PaletteOptions['primary'];
    copyLegalSmall?: PaletteOptions['primary'];
    copyTextLink?: PaletteOptions['primary'];

    button?: PaletteOptions['primary'];
    label?: PaletteOptions['primary'];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    black: true;
    button: true;
    label: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    titleBarHeader: true;
    copyHeader: true;
    copyBody: true;
    copyLegalSmall: true;
    copyTextLink: true;
  }
}

interface Props {
  children: ReactNode;
}

const WhiteLabelThemeProvider = ({children}: Props) => {
  const [brandedTheme, setBrandedTheme] = useState(appTheme);

  const brandingResponse = useSelector(getBranding);

  useEffect(() => {
    [
      {url: brandingResponse?.titleBarHeaderFontUrl, name: brandingResponse?.titleBarHeaderFontName},
      {url: brandingResponse?.copyHeaderFontUrl, name: brandingResponse?.copyHeaderFontName},
      {url: brandingResponse?.copyBodyFontUrl, name: brandingResponse?.copyBodyFontName},
      {url: brandingResponse?.copyLegalSmallFontUrl, name: brandingResponse?.copyLegalSmallFontName},
      {url: brandingResponse?.copyTextLinkFontUrl, name: brandingResponse?.copyTextLinkFontName},
    ].forEach(({url, name}) => {
      if (url && name) {
        const fontLink = document.createElement('link');
        fontLink.setAttribute("rel", "stylesheet");
        fontLink.setAttribute("href", url);
        document.head.appendChild(fontLink);

        // Needed for non-Google fonts
        // If we ever need to configure font sizes/weights in the future,
        // that can be passed to below.
        const style = document.createElement('style');
        style.textContent = `
        @font-face {
          font-family: '${name}';
          src: url('${url}');
        }`;
        document.head.appendChild(style);
      }
    });

    setBrandedTheme(createTheme(brandedTheme, {
      typography: {
        ...appTheme.typography,
        titleBarHeader: {
          fontFamily: brandingResponse?.titleBarHeaderFontName,
          color: brandingResponse?.titleBarHeaderFontColor,
        },
        copyHeader: {
          fontFamily: brandingResponse?.copyHeaderFontName,
          color: brandingResponse?.copyHeaderFontColor,
        },
        copyBody: {
          fontFamily: brandingResponse?.copyBodyFontName,
          color: brandingResponse?.copyBodyFontColor,
        },
        copyLegalSmall: {
          fontFamily: brandingResponse?.copyLegalSmallFontName,
          color: brandingResponse?.copyLegalSmallFontColor,
        },
        copyTextLink: {
          fontFamily: brandingResponse?.copyTextLinkFontName,
          color: brandingResponse?.copyTextLinkFontColor,
        },
        h1: {
          fontFamily: brandingResponse?.copyHeaderFontName,
          color: brandingResponse?.copyHeaderFontColor,
        },
        h2: {
          fontFamily: brandingResponse?.copyHeaderFontName,
          color: brandingResponse?.copyHeaderFontColor,
        },
        h3: {
          fontFamily: brandingResponse?.copyHeaderFontName,
          color: brandingResponse?.copyHeaderFontColor,
        },
        h4: {
          fontFamily: brandingResponse?.copyHeaderFontName,
          color: brandingResponse?.copyHeaderFontColor,
        },
        h5: {
          fontFamily: brandingResponse?.copyHeaderFontName,
          color: brandingResponse?.copyHeaderFontColor,
        },
        h6: {
          fontFamily: brandingResponse?.copyHeaderFontName,
          color: brandingResponse?.copyHeaderFontColor,
        },
        subtitle1: {
          fontFamily: brandingResponse?.copyHeaderFontName,
          color: brandingResponse?.copyHeaderFontColor,
        },
        subtitle2: {
          fontFamily: brandingResponse?.copyHeaderFontName,
          color: brandingResponse?.copyHeaderFontColor,
        },
        body1: {
          fontFamily: brandingResponse?.copyBodyFontName,
          color: brandingResponse?.copyBodyFontColor,
        },
        body2: {
          fontFamily: brandingResponse?.copyBodyFontName,
          color: brandingResponse?.copyBodyFontColor,
        },
        caption: {
          fontFamily: brandingResponse?.copyLegalSmallFontName,
          color: brandingResponse?.copyLegalSmallFontColor,
        },
        overline: {
          fontFamily: brandingResponse?.copyTextLinkFontName,
          color: brandingResponse?.copyTextLinkFontColor,
        },
        button: {
          fontFamily: brandingResponse?.copyBodyFontName,
        }
      },
      palette: {
        ...appTheme.palette,
        titleBar: appTheme.palette.augmentColor({
          color: {
            main: brandingResponse?.titleBarHeaderFontColor || appTheme.palette.primary.main,
          }
        }),
        titleBarGradient1: {
          main: brandingResponse?.titleBarHeaderGradientBackground1,
        },
        titleBarGradient2: {
          main: brandingResponse?.titleBarHeaderGradientBackground2,
        },
        tipBox: appTheme.palette.augmentColor({
          color: {
            main: brandingResponse?.tipBoxBackgroundColor || appTheme.palette.primary.main,
          }
        }),
        copyHeader: {
          main: brandingResponse?.copyHeaderFontColor,
        },
        copyBody: {
          main: brandingResponse?.copyBodyFontColor,
        },
        copyLegalSmall: {
          main: brandingResponse?.copyLegalSmallFontColor,
        },
        copyTextLink: {
          main: brandingResponse?.copyTextLinkFontColor,
        },
        button: {
          ...appTheme.palette.augmentColor({
            color: {
              main: brandingResponse?.buttonColor || appTheme.palette.primary.main,
            },
          }), 
          contrastText: brandingResponse?.labelColor
        },
        label: {
          main: brandingResponse?.labelColor,
        },
      },
      components: {
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              color: brandingResponse?.copyBodyFontColor,
            },
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: {
              fontFamily: brandingResponse?.copyLegalSmallFontName,
            },
          }
        },  
        MuiCssBaseline: {
          styleOverrides: {
            'a, a:link, a:visited, a:hover, a:active': {
              color: brandingResponse?.copyTextLinkFontColor,
              fontFamily: brandingResponse?.copyTextLinkFontName,
            }
          },
        },
        MuiLink: {
          styleOverrides: {
            color: brandingResponse?.copyTextLinkFontColor,
            fontFamily: brandingResponse?.copyTextLinkFontName,
            'a, a:link, a:visited, a:hover, a:active': {
              color: brandingResponse?.copyTextLinkFontColor,
              fontFamily: brandingResponse?.copyTextLinkFontName,
            }
          },
        },
      }
    }));
  }, [brandingResponse]);

  return (
    <ThemeProvider theme={brandedTheme}>
      {children}
    </ThemeProvider>
  );
};

export default WhiteLabelThemeProvider;
